import { formatMusicalKey } from '../../utils/formatMusicalKey';

export type AgentAttachedTrack = {
  id: string;
  title: string;
  artist: string;
  bpm: number;
  key: string;
  albumCover: string;
};

export const formatAttachedTrackContext = (
  track: AgentAttachedTrack,
): string => {
  const key = formatMusicalKey(track.key);
  const bpm = track.bpm > 0 ? `${track.bpm} BPM` : null;
  const meta = [bpm, key].filter(Boolean).join(', ');
  const metaSuffix = meta ? ` (${meta})` : '';
  return `About this track: [${track.artist} — ${track.title}](ch://track/${track.id})${metaSuffix}.\n\n`;
};

const ATTACHED_TRACK_PREFIX =
  /^About this track: \[(.+?) — (.+?)\]\(ch:\/\/track\/([^)]+)\)(?: \(([^)]+)\))?\.?\s*(?:\n\n)?/;

export type ParsedAttachedTrackMessage = {
  track: {
    id: string;
    artist: string;
    title: string;
    bpm?: number;
    key?: string;
  };
  prompt: string;
};

const parseAttachedTrackMeta = (
  metaText?: string,
): { bpm?: number; key?: string } => {
  if (!metaText?.trim()) return {};
  let bpm: number | undefined;
  let key: string | undefined;

  for (const part of metaText.split(',').map((entry) => entry.trim())) {
    if (!part) continue;
    if (/bpm/i.test(part)) {
      const match = part.match(/(\d+)/);
      if (match) bpm = Number(match[1]);
      continue;
    }
    key = part;
  }

  return { bpm, key };
};

/** Pulls the seed-track card and prompt back out of a stored user message. */
export const parseAttachedTrackMessage = (
  content: string,
): ParsedAttachedTrackMessage | null => {
  const match = content.match(ATTACHED_TRACK_PREFIX);
  if (!match) return null;

  const [, artist, title, id, metaText] = match;
  const { bpm, key } = parseAttachedTrackMeta(metaText);

  return {
    track: {
      id,
      artist,
      title,
      ...(bpm ? { bpm } : {}),
      ...(key ? { key: formatMusicalKey(key) || key } : {}),
    },
    prompt: content.slice(match[0].length).trim(),
  };
};

export const QUICK_TRACK_RECOMMENDATIONS_PROMPT =
  'Give me quick mixable track recommendations for this seed track.';

const LEADING_TRACK_TOKEN = /^@\[([^\]]+)\]\s*\(ch:\/\/track\/([^)]+)\)\s*/;

const splitTrackLabel = (
  label: string,
): Pick<AgentAttachedTrack, 'artist' | 'title'> => {
  const split = label.split(' — ');
  if (split.length < 2) {
    return { artist: '', title: label.trim() };
  }
  const artist = split.shift()?.trim() ?? '';
  const title = split.join(' — ').trim();
  return { artist, title };
};

const trackFromToken = (
  label: string,
  id: string,
  tracksById: Map<string, AgentAttachedTrack>,
): AgentAttachedTrack => {
  const cached = tracksById.get(id);
  if (cached) return cached;
  return {
    id,
    albumCover: '',
    bpm: 0,
    key: '',
    ...splitTrackLabel(label),
  };
};

/** Turns composer draft text into the agent prompt (track context is sent once). */
export const formatDraftSubmission = (
  draft: string,
  tracksById: Map<string, AgentAttachedTrack> = new Map(),
): string | null => {
  const trimmed = draft.trim();
  if (!trimmed) return null;

  const match = trimmed.match(LEADING_TRACK_TOKEN);
  if (!match) return trimmed;

  const [, label, id] = match;
  const track = trackFromToken(label, id, tracksById);
  const rest = trimmed.slice(match[0].length).trim();
  const question = rest || QUICK_TRACK_RECOMMENDATIONS_PROMPT;
  return `${formatAttachedTrackContext(track)}${question}`;
};

export const resolveAttachedTrackPrompt = (
  prompt: string,
  track: AgentAttachedTrack | null,
): string | null => {
  const trimmed = prompt.trim();
  if (trimmed) return trimmed;
  if (!track) return null;
  return QUICK_TRACK_RECOMMENDATIONS_PROMPT;
};

/** Builds the agent prompt from a composer card attachment + optional question. */
export const formatAttachedTrackSubmission = (
  prompt: string,
  track: AgentAttachedTrack | null,
): string | null => {
  const question = resolveAttachedTrackPrompt(prompt, track);
  if (!question) return null;
  return track ? `${formatAttachedTrackContext(track)}${question}` : question;
};
